import type { StyleProp } from '@vtex/admin-ui'
import { color } from '@vtex/admin-ui'
import type {
  ConnectDropTarget,
  DropTargetHookSpec,
  FactoryOrInstance,
} from 'react-dnd'
import { useDrop } from 'react-dnd'

export function getDropHighlightStyles(
  canDrop: boolean,
  isOver: boolean
): StyleProp {
  let styles = {}

  if (canDrop && isOver) {
    styles = {
      border: '$mainSelected',
      borderStyle: 'dashed',
      backgroundColor: '$action.main.tertiarySelected',
    }
  } else if (canDrop) {
    styles = {
      border: `2px dashed ${color('$gray20')}`,
    }
  } else {
    styles = {
      border: '2px solid transparent',
    }
  }

  return {
    transitionProperty: 'border, background-color, border-style',
    transitionDuration: '0.3s',
    transitionTimingFunction: 'ease-out',
    ...styles,
  }
}

export function useDropWithHighlights<DragObject, DropResult, CollectedProps>(
  spec: FactoryOrInstance<
    DropTargetHookSpec<DragObject, DropResult, CollectedProps>
  >,
  deps?: unknown[]
): [StyleProp, ConnectDropTarget] {
  if (typeof spec === 'function') {
    spec = spec()
  }

  const [{ canDrop, isOver }, dropTarget] = useDrop<
    DragObject,
    DropResult,
    { isOver: boolean; canDrop: boolean }
  >(
    {
      ...spec,
      collect: (monitor) => ({
        canDrop: monitor.canDrop(),
        isOver: monitor.isOver(),
      }),
    },
    deps
  )

  return [getDropHighlightStyles(canDrop, isOver), dropTarget]
}
